import React, { useState, useEffect } from "react";
import { History, CheckCircle, Phone, Event, AttachMoney, Notes } from "@mui/icons-material";
import apiService from "../../services/apiService";

const ResolutionsTab = ({ loanId }) => {
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loanId) {
      fetchResolutions();
    }
  }, [loanId]);

  const fetchResolutions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/loan-resolutions`,
        { loanId }
      );

      if (response.ok) {
        const data = await response.json();
        setResolutions(data.resolutions || []);
      } else {
        setError("Failed to load resolutions");
      }
    } catch (err) {
      console.error("Error fetching resolutions:", err);
      setError("An error occurred while loading resolutions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return "N/A";
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getResolutionTypeLabel = (type) => {
    const labels = {
      client_picked_up: "Client Picked Up",
      client_no_answer: "Client Didn't Pick Up",
      promise_to_pay: "Promise to Pay",
      payment_collected: "Payment Collected",
      resolved: "Resolved",
      wrong_number: "Wrong Number",
      dispute: "Dispute",
      other: "Other"
    };
    return labels[type] || type;
  };

  const getResolutionTypeBadgeColor = (type) => {
    const colors = {
      client_picked_up: "bg-green-100 text-green-800",
      client_no_answer: "bg-yellow-100 text-yellow-800",
      promise_to_pay: "bg-blue-100 text-blue-800",
      payment_collected: "bg-green-100 text-green-800",
      resolved: "bg-purple-100 text-purple-800",
      wrong_number: "bg-red-100 text-red-800",
      dispute: "bg-orange-100 text-orange-800",
      other: "bg-gray-100 text-gray-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading resolution history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <History className="mx-auto text-red-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-red-700 mb-2">Error Loading Resolutions</h3>
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchResolutions}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!resolutions || resolutions.length === 0) {
    return (
      <div className="p-12 text-center">
        <History className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Resolutions</h3>
        <p className="text-gray-500">
          No collection resolutions have been submitted for this loan yet.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <History className="text-blue-600" />
          Resolution History ({resolutions.length})
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Track of all collection call resolutions for this loan
        </p>
      </div>

      {/* Resolutions Timeline */}
      <div className="space-y-4">
        {resolutions.map((resolution, index) => (
          <div
            key={resolution._id || index}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <CheckCircle className="text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getResolutionTypeBadgeColor(resolution.resolutionType)}`}>
                      {getResolutionTypeLabel(resolution.resolutionType)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <Phone fontSize="small" style={{ fontSize: 16, verticalAlign: "middle" }} className="mr-1" />
                    Submitted by <span className="font-medium text-gray-900">{resolution.agentName}</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-900 font-medium">
                  {formatDate(resolution.createdAt)}
                </p>
              </div>
            </div>

            {/* Promise to Pay Details */}
            {resolution.resolutionType === "promise_to_pay" && resolution.promiseDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                      <Event fontSize="small" style={{ fontSize: 14 }} />
                      Promise Date
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatDate(resolution.promiseDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                      <AttachMoney fontSize="small" style={{ fontSize: 14 }} />
                      Promise Amount
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(resolution.promiseAmount)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Collected Details */}
            {resolution.resolutionType === "payment_collected" && resolution.promiseAmount && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                  <AttachMoney fontSize="small" style={{ fontSize: 14 }} />
                  Amount Collected
                </p>
                <p className="text-base font-semibold text-green-700">
                  {formatCurrency(resolution.promiseAmount)}
                </p>
              </div>
            )}

            {/* Notes */}
            <div>
              <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                <Notes fontSize="small" style={{ fontSize: 14 }} />
                Notes
              </p>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200 whitespace-pre-wrap">
                {resolution.notes || "No notes provided"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResolutionsTab;
